import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { User } from "oidc-client-ts";
import { authService } from "./authService";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (returnUrl?: string, prompt?: string) => Promise<void>;
  completeSignIn: () => Promise<User>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    authService
      .getUser()
      .then((loadedUser) => {
        if (mounted) {
          setUser(loadedUser);
          if (loadedUser) {
            localStorage.setItem("user", JSON.stringify(loadedUser));
            localStorage.setItem(
              "userInfo",
              JSON.stringify(loadedUser.profile ?? {})
            );
          }
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const manager = authService.userManager;
    const onUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
      localStorage.setItem("user", JSON.stringify(loadedUser));
      localStorage.setItem("userInfo", JSON.stringify(loadedUser.profile ?? {}));
    };
    const onUserUnloaded = () => {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("userInfo");
    };
    const onAccessTokenExpired = () => {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("userInfo");
    };

    manager.events.addUserLoaded(onUserLoaded);
    manager.events.addUserUnloaded(onUserUnloaded);
    manager.events.addAccessTokenExpired(onAccessTokenExpired);

    return () => {
      mounted = false;
      manager.events.removeUserLoaded(onUserLoaded);
      manager.events.removeUserUnloaded(onUserUnloaded);
      manager.events.removeAccessTokenExpired(onAccessTokenExpired);
    };
  }, []);

  // Stable references — must never change so effects that depend on these
  // (AuthCallback, AccountLogin) don't re-run and double-invoke OIDC calls.
  const signIn = useCallback(
    (returnUrl?: string, prompt?: string) => authService.signIn(returnUrl, prompt),
    []
  );
  const completeSignIn = useCallback(() => authService.completeSignIn(), []);
  const signOut = useCallback(() => {
    // Flag checked by login page to force prompt=login on next signIn,
    // preventing the IDP from silently re-authenticating via its session cookie.
    sessionStorage.setItem("post_logout", "1");
    localStorage.removeItem("biznifyBusinessProfile");
    localStorage.removeItem("clientBusinessProfile");
    localStorage.removeItem("profileId");
    localStorage.removeItem("userId");
    void authService.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user && !user.expired,
      signIn,
      completeSignIn,
      signOut,
    }),
    [user, loading, signIn, completeSignIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
